import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AutoScrollToErrorField, Button, ServerForm, ServerFormSectionGrey, ProviderPrimaryCptCodeAutocomplete, StickyActionBar } from '@uplift/ui-v2';
import { Form, useFormikContext } from 'formik';
import { DiagnosisSelect } from '../../../components';
import { NoteSection } from '../components/NoteSection';
import { RiskSection } from './risk-section';
import { CommNoteFormType } from './CommunicationNotePage';
import { Diagnosis, ProviderMedicalRole } from '@uplift/types';
import { MedicalNoteForm } from '@uplift/api';
import { useMemo } from 'react';

export type CommNoteFormProps = {
  commNoteForm: MedicalNoteForm;
  diagnoses: Diagnosis[];
  displayName: string;
  medicalRole: ProviderMedicalRole;
  riskAssessmentForm: MedicalNoteForm;
  userId: string;
  handleDelete: () => void;
};

export const CommNoteForm: React.VFC<CommNoteFormProps> = ({
  commNoteForm,
  diagnoses,
  displayName,
  medicalRole,
  riskAssessmentForm,
  userId,
  handleDelete
}) => {
  const { classes } = useStyles();
  const formik = useFormikContext<CommNoteFormType>();
  const detailsSection = commNoteForm.fields.filter(x => x.id !== 'summary' && !x.id.includes('billable'));
  const billingSection = commNoteForm.fields.filter(x => x.id.includes('billable'));
  const summarySection = commNoteForm.fields.filter(x => x.id === 'summary');
  const codesToRemove = [ '90837', '90834', '90847', '90846', '90849', '90791' ];
  const contactReason = formik.values.comm['contact_reason'];
  const contactMethod = formik.values.comm['contact_method'];

  const showBillable = useMemo(() => {
    if ((contactReason === 'crisis_contact' || contactReason === 'family_contact') && contactMethod === 'phone') {
      return true;
    }
    formik.setFieldValue('comm.billable', false);
    return false;
  }, [ contactMethod, contactReason ]);

  return (
    <Form>
      <AutoScrollToErrorField />
      <Box className={classes.content}>
        <Typography variant="h3" className={classes.title}>
          Communication Note
        </Typography>
        <Box display="flex" flexDirection="column" gap="24px" mb={8}>
          <NoteSection title={displayName} />
          <NoteSection title="Details" className={classes.details}>
            <ServerForm fields={detailsSection} namespace="comm" />
          </NoteSection>
          <RiskSection fields={riskAssessmentForm.fields} />
          <NoteSection title="Summary">
            <Box pb={3}>
              <ServerForm fields={summarySection} namespace="comm" />
            </Box>
          </NoteSection>
          {showBillable && (
            <NoteSection title="Billing">
              <Box pb={formik.values.comm['billable'] ? 0 : 3}>
                <ServerForm fields={billingSection} namespace="comm"/>
              </Box>
              {formik.values.comm['billable'] && (
                <Box mt={2}>
                  <ServerFormSectionGrey>
                    <Typography variant="overline" color="secondary">CPT Code</Typography>
                    <ProviderPrimaryCptCodeAutocomplete
                      name="cptCodes"
                      providerId={userId}
                      medicalRole={medicalRole}
                      ungroupCptCodes={true}
                      filterOutCptCodes={codesToRemove}
                    />
                    <Typography variant="overline" color="secondary">Diagnosis</Typography>
                    <Box pb={3}>
                      <DiagnosisSelect
                        name="diagnosis"
                        diagnoses={diagnoses}
                        color="secondary"
                        chipClassName={classes.chip}
                      />
                    </Box>
                  </ServerFormSectionGrey>
                </Box>
              )}
            </NoteSection>
          )}
        </Box>
      </Box>
      <StickyActionBar
        leftContainer={(
          <Button
            variant="outlined"
            color="secondary"
            disabled={formik.isSubmitting}
            onClick={handleDelete}
          >
            Delete Note
          </Button>
        )}
      >
        <Button
          variant="outlined"
          color="secondary"
          disabled={formik.isSubmitting}
          onClick={async () => {
            await formik.setFieldValue('submit', 'later');
            formik.handleSubmit();
          }}
        >
          Save and finish later
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={formik.isSubmitting}
          onClick={async () => {
            await formik.setFieldValue('submit', 'now');
            formik.handleSubmit();
          }}
        >
          Sign and submit
        </Button>
      </StickyActionBar>
    </Form>
  );
};

const useStyles = makeStyles({ name: 'CommunicationNotePage' })(theme => ({
  card: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 3),
    width: '100%'
  },
  chip: {
    backgroundColor: theme.palette.grey[300],
  },
  content: {
    marginLeft: theme.spacing(15),
    marginTop: theme.spacing(8),
    paddingBottom: theme.spacing(15),
    width: '792px',
  },
  title: {
    marginBottom: theme.spacing(8),
  },
  details: {
    maxWidth: '400px'
  }
}));
